/**
 * CategoryContext
 *
 * Global context for managing category and thumbnail data across the application.
 * Provides caching and refresh capabilities to avoid repeated API calls.
 * Uses localStorage for persistent caching with TTL.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { Category, ClassThumbnail } from '@/types'
import { apiService } from '@/services/api'
import { useAuth } from '@/contexts/AuthContext'

// Cache configuration
const CACHE_KEYS = {
  CATEGORIES: 'lumis_categories_cache',
  CLASS_CATEGORIES: 'lumis_class_categories_cache',
  THUMBNAILS: 'lumis_thumbnails_cache',
  TIMESTAMP: 'lumis_cache_timestamp'
}
const CACHE_TTL_MS = 1000 * 60 * 60 // 1 hour cache TTL

interface CachedData<T> {
  data: T
  timestamp: number
}

// Helper to get cached data from localStorage
function getCachedData<T>(key: string): T | null {
  try {
    const cached = localStorage.getItem(key)
    if (!cached) return null

    const parsed: CachedData<T> = JSON.parse(cached)
    const now = Date.now()

    // Check if cache is still valid
    if (now - parsed.timestamp < CACHE_TTL_MS) {
      return parsed.data
    }

    // Cache expired, remove it
    localStorage.removeItem(key)
    return null
  } catch (err) {
    console.warn('Failed to read cache:', key, err)
    return null
  }
}

// Helper to set cached data in localStorage
function setCachedData<T>(key: string, data: T): void {
  try {
    const cached: CachedData<T> = {
      data,
      timestamp: Date.now()
    }
    localStorage.setItem(key, JSON.stringify(cached))
  } catch (err) {
    console.warn('Failed to write cache:', key, err)
  }
}

// Helper to clear all caches
function clearAllCaches(): void {
  Object.values(CACHE_KEYS).forEach(key => {
    localStorage.removeItem(key)
  })
}

interface CategoryContextType {
  // Category data
  categories: Record<string, Category & { classCount: number; classes: string[] }>
  classCategories: Record<string, Category | null>

  // Thumbnail data
  classThumbnails: Record<string, ClassThumbnail | null>

  // Loading states
  loading: boolean
  error: string | null

  // Actions
  refreshCategories: (forceRefresh?: boolean) => Promise<void>
  refreshThumbnails: (forceRefresh?: boolean) => Promise<void>
  refreshThumbnailForClass: (classId: string) => Promise<void>
  refreshAll: (forceRefresh?: boolean) => Promise<void>
  clearCache: () => void

  // Helper functions
  getClassCategory: (classId: string) => Category | null
  getClassThumbnail: (classId: string) => ClassThumbnail | null
}

const CategoryContext = createContext<CategoryContextType | undefined>(undefined)

export function CategoryProvider({ children }: { children: React.ReactNode }) {
  const { isAdmin } = useAuth()
  const [categories, setCategories] = useState<Record<string, Category & { classCount: number; classes: string[] }>>({})
  const [classCategories, setClassCategories] = useState<Record<string, Category | null>>({})
  const [classThumbnails, setClassThumbnails] = useState<Record<string, ClassThumbnail | null>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Ref to track if initial load has been done
  const initialLoadDone = useRef(false)
  // Track if we've loaded thumbnails for admin
  const adminThumbnailsLoaded = useRef(false)

  // Refresh categories from API
  const refreshCategories = useCallback(async (forceRefresh: boolean = false) => {
    try {
      setError(null)

      // Check cache first (unless force refresh)
      if (!forceRefresh) {
        const cachedCategories = getCachedData<Record<string, Category & { classCount: number; classes: string[] }>>(CACHE_KEYS.CATEGORIES)
        const cachedClassCategories = getCachedData<Record<string, Category | null>>(CACHE_KEYS.CLASS_CATEGORIES)

        if (cachedCategories && cachedClassCategories) {
          setCategories(cachedCategories)
          setClassCategories(cachedClassCategories)
          return
        }
      }

      // Get all categories from API
      const categoriesData = await apiService.getAllCategories()
      setCategories(categoriesData)

      // Build class -> category mapping (store both underscore and space formats for flexible lookup)
      const classCategoryMap: Record<string, Category | null> = {}
      Object.values(categoriesData).forEach((categoryWithClasses) => {
        categoryWithClasses.classes.forEach((className) => {
          const category = {
            name: categoryWithClasses.name,
            color: categoryWithClasses.color,
            icon: categoryWithClasses.icon
          }
          // Store with original format (underscores from folder names)
          classCategoryMap[className] = category
          // Also store with spaces format for lookups from display names
          const withSpaces = className.replace(/_/g, ' ')
          if (withSpaces !== className) {
            classCategoryMap[withSpaces] = category
          }
        })
      })
      setClassCategories(classCategoryMap)

      // Cache the results
      setCachedData(CACHE_KEYS.CATEGORIES, categoriesData)
      setCachedData(CACHE_KEYS.CLASS_CATEGORIES, classCategoryMap)

    } catch (err) {
      console.error('Failed to refresh categories:', err)
      setError('Failed to load categories')
    }
  }, [])

  // Refresh thumbnails from API (loads for all classes)
  // Only admin users have access to the enrollment/classes endpoint
  const refreshThumbnails = useCallback(async (forceRefresh: boolean = false) => {
    // Skip thumbnail loading for non-admin users to avoid 403 errors
    if (!isAdmin) {
      return
    }

    try {
      setError(null)

      // Check cache first (unless force refresh)
      if (!forceRefresh) {
        const cachedThumbnails = getCachedData<Record<string, ClassThumbnail | null>>(CACHE_KEYS.THUMBNAILS)

        if (cachedThumbnails && Object.keys(cachedThumbnails).length > 0) {
          // Update URLs with fresh token
          const token = localStorage.getItem('auth_token')
          const updatedThumbnails: Record<string, ClassThumbnail | null> = {}

          Object.entries(cachedThumbnails).forEach(([classId, thumbnail]) => {
            if (thumbnail && thumbnail.url) {
              // Cached URL has class_id and filename, just add token and timestamp
              const separator = thumbnail.url.includes('?') ? '&' : '?'
              updatedThumbnails[classId] = {
                ...thumbnail,
                url: `${thumbnail.url}${separator}token=${token}&t=${Date.now()}`
              }
            } else {
              updatedThumbnails[classId] = thumbnail
            }
          })

          setClassThumbnails(updatedThumbnails)
          return
        }
      }

      // Get all classes
      const classes = await apiService.getEnrollmentClasses()

      // Load thumbnails for all classes in parallel
      const thumbnailPromises = classes.map(async (cls: any) => {
        try {
          const thumbnail = await apiService.getClassThumbnail(cls.id)
          // Append auth token for <img> tag usage
          const token = localStorage.getItem('auth_token')
          const withToken = thumbnail && thumbnail.url ? {
            ...thumbnail,
            url: `${thumbnail.url}${thumbnail.url.includes('?') ? '&' : '?'}token=${token}&t=${Date.now()}`
          } : thumbnail
          return { classId: cls.id, thumbnail: withToken }
        } catch (err) {
          // If thumbnail doesn't exist, that's okay
          return { classId: cls.id, thumbnail: null }
        }
      })

      const thumbnailResults = await Promise.all(thumbnailPromises)

      // Build class -> thumbnail mapping (store both underscore and space formats for flexible lookup)
      const classThumbnailMap: Record<string, ClassThumbnail | null> = {}
      thumbnailResults.forEach(({ classId, thumbnail }) => {
        // Store with original format (underscores from folder names)
        classThumbnailMap[classId] = thumbnail
        // Also store with spaces format for lookups from display names
        const withSpaces = classId.replace(/_/g, ' ')
        if (withSpaces !== classId) {
          classThumbnailMap[withSpaces] = thumbnail
        }
      })
      setClassThumbnails(classThumbnailMap)

      // Cache the results (store URLs without token/timestamp for security, store both formats)
      const cacheableThumbnails: Record<string, ClassThumbnail | null> = {}
      thumbnailResults.forEach(({ classId, thumbnail }) => {
        let cacheableThumbnail: ClassThumbnail | null = thumbnail
        if (thumbnail && thumbnail.url) {
          // Parse URL and remove token/timestamp params, keep class_id and filename
          try {
            const url = new URL(thumbnail.url, window.location.origin)
            url.searchParams.delete('token')
            url.searchParams.delete('t')
            cacheableThumbnail = {
              ...thumbnail,
              url: url.pathname + url.search // Store path with essential params only
            }
          } catch {
            // Keep original if URL parsing fails
          }
        }
        // Store with original format (underscores)
        cacheableThumbnails[classId] = cacheableThumbnail
        // Also store with spaces format
        const withSpaces = classId.replace(/_/g, ' ')
        if (withSpaces !== classId) {
          cacheableThumbnails[withSpaces] = cacheableThumbnail
        }
      })
      setCachedData(CACHE_KEYS.THUMBNAILS, cacheableThumbnails)

    } catch (err) {
      console.error('Failed to refresh thumbnails:', err)
      setError('Failed to load thumbnails')
    }
  }, [isAdmin])

  // Refresh thumbnail for a single class (avoid fetching all)
  // This endpoint is accessible to all authenticated users
  const refreshThumbnailForClass = useCallback(async (classId: string) => {
    try {
      setError(null)
      const thumbnail = await apiService.getClassThumbnail(classId)
      const token = localStorage.getItem('auth_token')
      const withToken = thumbnail && thumbnail.url ? {
        ...thumbnail,
        url: `${thumbnail.url}${thumbnail.url.includes('?') ? '&' : '?'}token=${token}&t=${Date.now()}`
      } : thumbnail
      setClassThumbnails(prev => {
        const updated = { ...prev, [classId]: withToken }
        // Update cache too - keep class_id and filename, remove token/t
        const cachedThumbnails = getCachedData<Record<string, ClassThumbnail | null>>(CACHE_KEYS.THUMBNAILS) || {}
        if (thumbnail && thumbnail.url) {
          try {
            const url = new URL(thumbnail.url, window.location.origin)
            url.searchParams.delete('token')
            url.searchParams.delete('t')
            cachedThumbnails[classId] = {
              ...thumbnail,
              url: url.pathname + url.search
            }
          } catch {
            cachedThumbnails[classId] = thumbnail
          }
        } else {
          cachedThumbnails[classId] = thumbnail
        }
        setCachedData(CACHE_KEYS.THUMBNAILS, cachedThumbnails)
        return updated
      })
    } catch (err) {
      console.error('Failed to refresh class thumbnail:', err)
      // If fails, set to null but do not blow away others
      setClassThumbnails(prev => ({ ...prev, [classId]: null }))
    }
  }, [])

  // Refresh all data
  const refreshAll = useCallback(async (forceRefresh: boolean = false) => {
    setLoading(true)
    try {
      await Promise.all([
        refreshCategories(forceRefresh),
        refreshThumbnails(forceRefresh)
      ])
    } finally {
      setLoading(false)
    }
  }, [refreshCategories, refreshThumbnails])

  // Clear all caches
  const clearCache = useCallback(() => {
    clearAllCaches()
    // Reset state
    setCategories({})
    setClassCategories({})
    setClassThumbnails({})
    // Reset load tracking refs so data can be reloaded
    initialLoadDone.current = false
    adminThumbnailsLoaded.current = false
  }, [])

  // Helper to get category for a class
  const getClassCategory = useCallback((classId: string): Category | null => {
    return classCategories[classId] || null
  }, [classCategories])

  // Helper to get thumbnail for a class
  const getClassThumbnail = useCallback((classId: string): ClassThumbnail | null => {
    return classThumbnails[classId] || null
  }, [classThumbnails])

  // Initial load - only run once for categories
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true
      // Load categories (available to all users)
      refreshCategories(false)
    }
  }, [refreshCategories])

  // Load thumbnails when isAdmin becomes true (handles async auth loading)
  useEffect(() => {
    if (isAdmin && !adminThumbnailsLoaded.current) {
      adminThumbnailsLoaded.current = true
      refreshThumbnails(false)
    }
  }, [isAdmin, refreshThumbnails])

  const value: CategoryContextType = {
    categories,
    classCategories,
    classThumbnails,
    loading,
    error,
    refreshCategories,
    refreshThumbnails,
    refreshThumbnailForClass,
    refreshAll,
    clearCache,
    getClassCategory,
    getClassThumbnail
  }

  return (
    <CategoryContext.Provider value={value}>
      {children}
    </CategoryContext.Provider>
  )
}

// Hook to use category context
export function useCategoryContext() {
  const context = useContext(CategoryContext)
  if (context === undefined) {
    throw new Error('useCategoryContext must be used within a CategoryProvider')
  }
  return context
}
